import { LessonRunner } from './core/LessonRunner';
import { NodeRegistry } from './nodes/NodeRegistry';
import { prepareTextNode, commitTextNode } from './nodes/types/TextNode';
import { prepareDividerNode, commitDividerNode } from './nodes/types/DividerNode';
import { prepareTableFillNode, commitTableFillNode } from './nodes/types/TableFillNode';
import { prepareDiagramStrokeNode, commitDiagramStrokeNode } from './nodes/types/DiagramStrokeNode';
import { prepareMultiRegionNode, commitMultiRegionNode } from './nodes/types/MultiRegionNode';
import { prepareRegionComposeNode, commitRegionComposeNode } from './nodes/types/RegionComposeNode';
import { prepareImageDrawNode, commitImageDrawNode } from './nodes/types/ImageDrawNode';
import { prepareSketchPathNode, commitSketchPathNode } from './nodes/types/SketchPathNode';
import { prepareEquationNode, commitEquationNode } from './nodes/types/EquationNode';
import { prepareBoxedDemoNode, commitBoxedDemoNode } from './nodes/types/BoxedDemoNode';
import {
  preparePhysicsDiagramNode,
  commitPhysicsDiagramNode,
} from './nodes/types/PhysicsDiagramNode';
import {
  preparePedagogicalDiagramNode,
  commitPedagogicalDiagramNode,
} from './nodes/types/PedagogicalDiagramNode';
import { setChalkTheme } from './primitives/chalk';
import { validateLessonSpec } from './schema/lessonSchema';

function createDefaultRegistry() {
  const registry = new NodeRegistry();

  registry.register('TEXT', {
    prepare: prepareTextNode,
    commit: commitTextNode,
  });

  registry.register('DIVIDER', {
    prepare: prepareDividerNode,
    commit: commitDividerNode,
  });

  registry.register('TABLE_FILL', {
    prepare: prepareTableFillNode,
    commit: commitTableFillNode,
  });

  registry.register('DIAGRAM_STROKE', {
    prepare: prepareDiagramStrokeNode,
    commit: commitDiagramStrokeNode,
  });

  registry.register('MULTI_REGION', {
    prepare: (env, spec) => prepareMultiRegionNode(env, spec, registry),
    commit: commitMultiRegionNode,
  });

  // Backward-compatible alias.
  registry.register('MULTI_COLUMN', {
    prepare: (env, spec) => prepareMultiRegionNode(env, spec, registry),
    commit: commitMultiRegionNode,
  });

  registry.register('PHYSICS_DIAGRAM', {
    prepare: preparePhysicsDiagramNode,
    commit: commitPhysicsDiagramNode,
  });

  registry.register('PEDAGOGICAL_DIAGRAM', {
    prepare: preparePedagogicalDiagramNode,
    commit: commitPedagogicalDiagramNode,
  });

  registry.register('REGION_COMPOSE', {
    prepare: (env, spec) => prepareRegionComposeNode(env, spec, registry),
    commit: commitRegionComposeNode,
  });

  registry.register('IMAGE_DRAW', {
    prepare: prepareImageDrawNode,
    commit: commitImageDrawNode,
  });

  registry.register('SKETCH_PATH', {
    prepare: prepareSketchPathNode,
    commit: commitSketchPathNode,
  });

  registry.register('EQUATION', {
    prepare: prepareEquationNode,
    commit: commitEquationNode,
  });

  registry.register('BOXED_DEMO', {
    prepare: (env, spec) => prepareBoxedDemoNode(env, spec, registry),
    commit: commitBoxedDemoNode,
  });

  return registry;
}

export function startLesson(canvas, lessonSpec) {
  const validatedSpec = validateLessonSpec(lessonSpec);
  const registry = createDefaultRegistry();
  const unknownKinds = validatedSpec.nodes
    .map((node) => node.kind)
    .filter((kind, i, arr) => arr.indexOf(kind) === i && !registry.has(kind));

  if (unknownKinds.length > 0) {
    throw new Error(`Unknown node kinds: ${unknownKinds.join(', ')}`);
  }

  setChalkTheme(validatedSpec.theme.chalk);
  const runner = new LessonRunner(canvas, validatedSpec, registry);

  runner.start();

  return {
    restart: () => runner.restart(),
    destroy: () => runner.destroy(),
    jumpTo: (nodeId) => runner.jumpTo(nodeId),
    setSpeed: (factor) => runner.setSpeed(factor),
    pause: () => runner.pause(),
    resume: () => runner.resume(),
    togglePause: () => runner.togglePause(),
    isPaused: () => runner.isPaused(),
    setManipulateMode: (enabled) => runner.setManipulateMode(enabled),
    toggleManipulateMode: () => runner.toggleManipulateMode(),
    isManipulateMode: () => runner.isManipulateMode(),
    setRegionGuides: (enabled) => runner.setRegionGuides(enabled),
    toggleRegionGuides: () => runner.toggleRegionGuides(),
    isRegionGuidesEnabled: () => runner.isRegionGuidesEnabled(),
    getZoomTargets: () => runner.getZoomTargets(),
    zoomToSlot: (slotIndex) => runner.zoomToSlot(slotIndex),
    zoomToNode: (nodeId) => runner.zoomToNode(nodeId),
    jumpToCheckpoint: (eventId) => runner.jumpToCheckpoint(eventId),
    getCheckpointIds: () => runner.getCheckpointIds(),
    getCheckpoints: () => runner.getCheckpoints(),
    getDebugSnapshot: () => runner.getDebugSnapshot(),
  };
}

export function loadLesson(spec) {
  return validateLessonSpec(spec);
}

export { NodeRegistry } from './nodes/NodeRegistry';
export { validateLessonSpec } from './schema/lessonSchema';
